import React, { useState, useEffect, CSSProperties } from "react";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import { FoundationsButton } from "./button";

interface DialogButton {
  label: string;
  onClick: () => void;
  className?: string;
  id?: string; 
  CustomDivs?: React.ReactNode;
  disabled?: boolean;
  customStyles?: CSSProperties;
  isLoading?: boolean;
}

interface DialogProps {
  isOpen?: boolean;
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  draggable?: boolean;
  resizable?: boolean;
  modal?: boolean;
  autoOpen?: boolean;
  buttons?: DialogButton[];
  closeOnEscape?: boolean;
  closeText?: string;
  width?: number | string;
  height?: number | string;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  position?: { x: number; y: number };
  zIndex?: number;
  showAnimation?: CSSProperties;
  hideAnimation?: CSSProperties;
  className?: string;
  setDialogOpen?: any; 
  contentClasses?: string;
  contentStyles?: CSSProperties;
  ButtonDivClasses?: string;
  hideClose?:boolean;
  loading?: boolean;
  loadingText?: string;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen = false,
  title = "Dialog Title",
  children,
  onClose,
  draggable = false,
  resizable = false,
  modal = true,
  autoOpen = true,
  buttons = [],
  closeOnEscape = true,
  closeText = "Close",
  width = "auto",
  height = "auto",
  minWidth = 150,
  minHeight = 150,
  maxWidth,
  maxHeight,
  position = { x: 0, y: 0 },
  zIndex = 1000,
  showAnimation,
  hideAnimation,
  className = "",
  setDialogOpen,
  contentClasses = "",
  contentStyles,
  ButtonDivClasses="",
  hideClose=false,
  loading = false,
  loadingText = "Loading...",

}) => {
   const [isDialogOpen, setIsDialogOpen] = useState(autoOpen && isOpen);

  const handleClose = () => {
    if (hideAnimation) {
      setIsDialogOpen(false);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    } else {
      setIsDialogOpen(false);
      if (onClose) onClose();
    }
  };

  useEffect(() => { 
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape && modal && isDialogOpen) {
        e.preventDefault();
        e.stopPropagation();
        if (!hideClose) {
          handleClose();
        }
      }
    };
  
    document.addEventListener("keydown", handleEsc, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleEsc, { capture: true });
    };
  }, [closeOnEscape, modal, isDialogOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsDialogOpen(true);
    } else {
      setIsDialogOpen(false);
    }
  }, [isOpen]);


  if (!isDialogOpen) return null;

  const dialogStyles = showAnimation && isDialogOpen ? showAnimation : {};
  const dialogHideStyles = hideAnimation && !isDialogOpen ? hideAnimation : {};

  const dialogContent = (
    <div
      className={`ui-dialog ui-widget ui-widget-content ui-corner-all ${className}`}
      style={{
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        zIndex,
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflow: "hidden",
        ...dialogStyles,
        ...dialogHideStyles,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ui-dialog-titlebar ui-widget-header ui-corner-all ui-helper-clearfix">
        <span className="ui-dialog-title" style={{ textAlign: 'left'}}>{title}</span>
        {!hideClose && <FoundationsButton
          id="close-dialog-btn"
          buttonClasses="ui-button ui-corner-all ui-widget ui-button-icon-only ui-dialog-titlebar-close"
          onClick={handleClose}
          title="Close"
        >
          <span className="ui-button-icon ui-icon ui-icon-closethick"></span>
          <span className="ui-button-icon-space"> </span>
          {closeText}
        </FoundationsButton>}
      </div>

      {resizable ? (
        <ResizableBox
          width={typeof width === "number" ? width : 400}
          height={typeof height === "number" ? height : 300}
          minConstraints={[minWidth!, minHeight!]}
          maxConstraints={[maxWidth!, maxHeight!]}
          className={`ui-dialog-content ui-widget-content ${contentClasses}`}
        >
          <div style={{ overflowY: 'auto', maxHeight: '100%' }}>{children}</div>
        </ResizableBox>
      ) : (
        <div className={`popup-wrapper ui-dialog-content ui-widget-content ${contentClasses}`}  style={{
          cursor : 'default',
          flexGrow: 1, // Ensures content takes remaining space
          overflowY: "auto", // Enables scrolling inside the dialog
          maxHeight: 'calc(100% - 50px)',
          ...contentStyles
        }}>
          {children}
        </div>
      )}

      {buttons.length > 0 && (
        <div
          className={`ui-dialog-buttonpane ui-widget-content ui-helper-clearfix ${ButtonDivClasses}`}
          // style={{ display: 'flex', alignItems: 'center' }}
        >
          {/* Loading Icon on the far left */}
          {loading &&
          <span style={{ marginRight: 'auto', display: 'flex', alignItems: 'center' }}>
            <div
              id="generic_object_viewer_loading"
              className={'ajax-loading-box'}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span className="ajax-loading-image"></span>
              {loadingText}
            </div>
          </span>
}
          {/* Buttons */}
          {buttons.map((btn, index) => (
            <FoundationsButton
              key={index}
              id={btn.id || ""}
              buttonClasses={`ui-button ui-corner-all ui-widget ${btn.className}`}
              onClick={btn.onClick}
              disabled={btn.disabled}
            >
              {btn.label}
              {btn.CustomDivs && btn.CustomDivs}
            </FoundationsButton>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {modal && <div className="ui-widget-overlay" style={{ zIndex: zIndex - 1, cursor: 'default' }} onClick={(e) => e.stopPropagation()} />}
      {draggable ? (
        <Draggable defaultPosition={position}>
          {dialogContent}
        </Draggable>
      ) : (
        dialogContent
      )}
    </>
  );
};

export default Dialog;